import { useCallback, useEffect, useState } from 'react'
import { Button } from '../ui/Button'
import { withErrorBoundary } from '../shared/ErrorBoundary'
import { secureFetch } from '@/utils/SecureFetch'
import type { Modifier, ModifierConflict, ValidatedModifierSet } from '@/types/Modifier.types.ts'
import './modifier-selector.css'

interface ModifierSelectorProps {
	playbookId: string
	selectedModifiers: string[]
	onModifiersChange: (modifiers: string[]) => void
}

function ModifierSelectorInternal({
									  playbookId,
									  selectedModifiers,
									  onModifiersChange,
								  }: ModifierSelectorProps) {
	const [modifiers, setModifiers] = useState<Modifier[]>([])
	const [conflicts, setConflicts] = useState<ModifierConflict[]>([])
	const [isLoading, setIsLoading] = useState(false)
	const [conflictWarnings, setConflictWarnings] = useState<string[]>([])

	const loadModifiers = useCallback(async (signal: AbortSignal) => {
		setIsLoading(true)
		try {
			const [modifiersRes, conflictsRes] = await Promise.all([
				secureFetch(`/api/playbooks/${playbookId}/modifiers`, { signal }),
				secureFetch(`/api/playbooks/${playbookId}/modifiers/conflicts`, { signal }),
			])
			if (signal.aborted) return

			if (!modifiersRes.ok || !conflictsRes.ok) {
				console.error('Failed to load modifiers:', modifiersRes.status, conflictsRes.status)
				return
			}

			const [modifiersData, conflictsData] = await Promise.all([
				modifiersRes.json(),
				conflictsRes.json(),
			])
			if (signal.aborted) return

			setModifiers(modifiersData.modifiers || [])
			setConflicts(conflictsData.conflicts || [])
		} catch (error) {
			if (error instanceof Error && error.name === 'AbortError') return
			console.error('Failed to load modifiers:', error)
		} finally {
			if (!signal.aborted) {
				setIsLoading(false)
			}
		}
	}, [playbookId])

	useEffect(() => {
		const abortController = new AbortController()
		loadModifiers(abortController.signal)
		return () => abortController.abort()
	}, [loadModifiers])

	const checkConflicts = useCallback(async (signal: AbortSignal) => {
		try {
			const response = await secureFetch(
				`/api/playbooks/${playbookId}/modifiers/validate?modifiers=${encodeURIComponent(selectedModifiers.join(','))}`,
				{ signal },
			)
			if (signal.aborted) return

			if (!response.ok) {
				console.error('Failed to validate modifiers:', response.status)
				return
			}

			const data: ValidatedModifierSet = await response.json()
			if (signal.aborted) return

			if (!data.isValid && data.conflicts) {
				const warnings = data.conflicts.map(
					c => `${c.modifier_a} conflicts with ${c.modifier_b}: ${c.reason}`,
				)
				setConflictWarnings(warnings)
			} else {
				setConflictWarnings([])
			}
		} catch (error) {
			if (error instanceof Error && error.name === 'AbortError') return
			console.error('Failed to validate modifiers:', error)
		}
	}, [playbookId, selectedModifiers])

	useEffect(() => {
		const abortController = new AbortController()

		if (selectedModifiers.length > 1) {
			checkConflicts(abortController.signal)
		} else {
			setConflictWarnings([])
		}

		return () => abortController.abort()
	}, [selectedModifiers, checkConflicts])

	function toggleModifier(canonicalName: string) {
		if (selectedModifiers.includes(canonicalName)) {
			onModifiersChange(selectedModifiers.filter(m => m !== canonicalName))
		} else {
			onModifiersChange([...selectedModifiers, canonicalName])
		}
	}

	function isModifierConflicting(canonicalName: string): boolean {
		if (selectedModifiers.includes(canonicalName)) return false

		return conflicts.some(
			c =>
				(c.modifier_a === canonicalName && selectedModifiers.includes(c.modifier_b)) ||
				(c.modifier_b === canonicalName && selectedModifiers.includes(c.modifier_a)),
		)
	}

	// Group modifiers by type
	const modifiersByType = modifiers.reduce((acc, mod) => {
		const existing = acc[mod.modifier_type]
		if (!existing) {
			acc[mod.modifier_type] = [mod]
		} else {
			existing.push(mod)
		}
		return acc
	}, {} as Record<string, Modifier[]>)

	if (isLoading) {
		return <div className="modifier-selector__loading" role="status" aria-live="polite">Loading modifiers...</div>
	}

	return (
		<div className="modifier-selector">
			{conflictWarnings.length > 0 && (
				<div className="modifier-selector__conflict-banner" role="alert" aria-live="polite">
					<strong>Conflicts detected:</strong>
					<ul>
						{conflictWarnings.map((warning, idx) => (
							<li key={idx}>{warning}</li>
						))}
					</ul>
				</div>
			)}

			{Object.entries(modifiersByType).map(([type, mods]) => (
				<div key={type}>
					<h4 className="modifier-selector__group-title" id={`modifier-group-${type}`}>
						{type} Modifiers
					</h4>
					<div className="modifier-selector__chips" role="group" aria-labelledby={`modifier-group-${type}`}>
						{mods.map(mod => {
							const isSelected = selectedModifiers.includes(mod.canonical_name)
							const isConflicting = isModifierConflicting(mod.canonical_name)

							return (
								<Button
									key={mod.id}
									variant={isSelected ? 'default' : 'outline'}
									size="sm"
									className="modifier-selector__chip"
									onClick={() => toggleModifier(mod.canonical_name)}
									disabled={isConflicting}
									data-selected={isSelected}
									data-conflicting={isConflicting}
									aria-pressed={isSelected}
									title={mod.description || mod.display_name}
								>
									{mod.display_name}
								</Button>
							)
						})}
					</div>
				</div>
			))}
		</div>
	)
}

export const ModifierSelector = withErrorBoundary(ModifierSelectorInternal)
